import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  makeStyles,
  MenuItem,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useAlert } from "react-alert";
import { useParams } from "react-router-dom";

import {
  getFindById,
  UpdatePrivateSale,
} from "../../../adminModel/privateSale";
import { getGroup } from "../../../adminModel/group";
import Image from "./Image";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiCardHeader-title": {
      fontWeight: 600,
    },
  },

  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },

  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },

  field: {
    marginBottom: theme.spacing(2),
  },

  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },

  imageSection: {
    marginTop: theme.spacing(1),
  },

  actionBar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
  },

  updateButton: {
    minWidth: 160,
    height: 44,
    fontWeight: 600,
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const Create = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [getImage, setImage] = useState([]);
  const [group, setGroup] = useState([]);
  const [showSSS, setShoww] = useState("");

  const [values, setValues] = useState({
    privateSalesGroup: "",
    artist: "",
    signed_dated: "",
    medium: "",
    size: "",
    title: "",
    property: "",
    coa: "",
    provenance: "",
    exhibited: "",
    literature: "",
    write_up: "",
    lot_group: "",
    lot_details_html: "",
    alias: "",
  });

  // =========================
  // FETCH GROUPS
  // =========================
  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);

      const results = await getGroup();

      setGroup(results);
    } catch (error) {
      console.error("Fetch Group Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================
  // FETCH PRIVATE SALE ITEM
  // =========================
  const FetchAuction = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getFindById(id);

      setImage(response);

      const results =
        response.data && response.data.length > 0
          ? response.data[0]
          : {};

      setValues({
        privateSalesGroup: results.privateSalesGroup || "",
        artist: results.artist || "",
        signed_dated: results.signed_dated || "",
        medium: results.medium || "",
        size: results.size || "",
        title: results.title || "",
        property: results.property || "",
        coa: results.coa || "",
        provenance: results.provenance || "",
        exhibited: results.exhibited || "",
        literature: results.literature || "",
        write_up: results.write_up || "",
        lot_group: results.lot_group || "",
        lot_details_html: results.lot_details_html || "",
        alias: results.alias || "",
      });

      setShoww(
        results.isVisible !== undefined
          ? results.isVisible
          : ""
      );
    } catch (error) {
      console.error("Fetch Auction Error:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGroup();
    FetchAuction();
  }, [fetchGroup, FetchAuction]);

  // =========================
  // HANDLE CHANGE
  // =========================
  const handleChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // SUBMIT
  // =========================
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const PrivateSaleData = {
      ...values,
      isVisible: showSSS,
    };

    try {
      const result = await UpdatePrivateSale(
        id,
        PrivateSaleData
      );

      alert.success(
        result?.msg || "Updated Successfully!"
      );
    } catch (error) {
      console.error("Update Error:", error);

      alert.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <form
        onSubmit={handleFormSubmit}
        autoComplete="off"
        className={clsx(classes.root, className)}
        {...rest}
      >
        <Card elevation={1}>
          {/* =========================
              HEADER
          ========================= */}
          <CardHeader
            avatar={<EditIcon color="primary" />}
            title="Edit Private Sale Item"
            subheader="Update the item information and details"
          />

          <Divider />

          <CardContent>
            {/* =========================
                BASIC INFORMATION
            ========================= */}
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Basic Information
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Update the main details of this private sale item.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Group"
                  name="privateSalesGroup"
                  value={values.privateSalesGroup}
                  onChange={handleChange}
                  variant="outlined"
                  select
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                >
                  {group.map((val) => (
                    <MenuItem
                      key={val.id}
                      value={val.groupname}
                    >
                      {val.groupname}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Artist"
                  name="artist"
                  value={values.artist}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Medium"
                  name="medium"
                  value={values.medium}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Signed Dated"
                  name="signed_dated"
                  value={values.signed_dated}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Size"
                  name="size"
                  value={values.size}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Lot Group"
                  name="lot_group"
                  value={values.lot_group}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Alias"
                  name="alias"
                  value={values.alias}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>
            </Grid>

            <Box mt={3} mb={3}>
              <Divider />
            </Box>

            {/* =========================
                ADDITIONAL DETAILS
            ========================= */}
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Additional Details
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Add or update supporting information for the item.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Property"
                  name="property"
                  value={values.property}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="COA"
                  name="coa"
                  value={values.coa}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Exhibited"
                  name="exhibited"
                  value={values.exhibited}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Provenance"
                  name="provenance"
                  value={values.provenance}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Literature"
                  name="literature"
                  value={values.literature}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Write Up"
                  name="write_up"
                  value={values.write_up}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Lot Details HTML"
                  name="lot_details_html"
                  value={values.lot_details_html}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  minRows={5}
                  InputLabelProps={{ shrink: true }}
                  className={classes.field}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                >
                  <InputLabel id="visibility-label">
                    Show / Unshow
                  </InputLabel>

                  <Select
                    labelId="visibility-label"
                    value={showSSS}
                    onChange={(e) =>
                      setShoww(e.target.value)
                    }
                    label="Show / Unshow"
                  >
                    <MenuItem value={1}>
                      Show
                    </MenuItem>

                    <MenuItem value={2}>
                      Unshow
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box mt={3} mb={3}>
              <Divider />
            </Box>

            {/* =========================
                IMAGES
            ========================= */}
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Item Images
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              View existing images or upload new images for this item.
            </Typography>

            <Box className={classes.imageSection}>
              <Image ItemData={getImage} />
            </Box>
          </CardContent>

          <Divider />

          {/* =========================
              ACTION
          ========================= */}
          <Box className={classes.actionBar}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              className={classes.updateButton}
              disabled={loading}
            >
              {loading
                ? "Updating..."
                : "Update Details"}
            </Button>
          </Box>
        </Card>
      </form>

      {/* =========================
          LOADING
      ========================= */}
      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

Create.propTypes = {
  className: PropTypes.string,
};

export default Create;